import React, { useMemo } from 'react';
import { AnsweringSubscriptionTag } from '@bb/domain/dist/types';

import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import Tag, { TagProps } from '../../Tag';
import ToolTip from '../../Tooltip';

type ToolTipTextType = 'agent' | 'listing' | 'brokerage';

const SUBSCRIPTION_TAG_TO_COLOR_AND_LABEL_MAP: Record<
  AnsweringSubscriptionTag | AnsweringServiceType,
  {
    color: TagProps['color'];
    label: string;
    toolTipText?: (type: ToolTipTextType) => string;
  }
> = {
  [AnsweringSubscriptionTag.NS]: {
    color: 'red',
    label: 'N/S',
    toolTipText: type => `This ${type} is not subscribed`,
  },
  [AnsweringSubscriptionTag.ON]: {
    color: 'green',
    label: 'ON',
    toolTipText: type => `This ${type} is subscribed`,
  },
  [AnsweringSubscriptionTag.OFF]: {
    color: 'orange',
    label: 'OFF',
    toolTipText: type => `This ${type} has Appointment Desk turned off`,
  },
  // For Answering Service Cross Org Search only listings can have a subscription tag of UNC
  [AnsweringSubscriptionTag.UNC]: {
    color: 'red',
    label: 'Not BrokerBay',
    toolTipText: type =>
      `This ${type} is not represented by BrokerBay. Please ensure to book via the correct showing service.`,
  },
  [AnsweringServiceType.AppointmentDesk]: {
    color: 'blue',
    label: 'Appt Desk Only',
  },
  [AnsweringServiceType.LiveConciergePlus]: {
    color: 'green',
    label: 'Live Concierge',
  },
};

type SubscriptionTagProps = {
  subscriptionTag: AnsweringSubscriptionTag | AnsweringServiceType;
  toolTipTextType: ToolTipTextType;
};

const SubscriptionTag: React.FC<SubscriptionTagProps> = ({
  subscriptionTag,
  toolTipTextType,
}) => {
  useMemo(() => {
    SUBSCRIPTION_TAG_TO_COLOR_AND_LABEL_MAP[AnsweringSubscriptionTag.NS].label =
      'No Subscription';
  }, []);

  const { color, label, toolTipText } =
    SUBSCRIPTION_TAG_TO_COLOR_AND_LABEL_MAP[subscriptionTag];

  return (
    <ToolTip title={toolTipText ? toolTipText(toolTipTextType) : undefined}>
      <Tag color={color} data-cy={`${toolTipTextType}-subscription-tag`}>
        {label}
      </Tag>
    </ToolTip>
  );
};

export default SubscriptionTag;
